// Coordinator workspace panels (P2.4a): clients (business records without a
// login — ruling D), offerings, and cancellation / no-show terms versions
// (ruling F: drafts by any coordinator; Activate is shown only to the company
// owner, and commerce allows it only for the owner and only for a complete
// version). No amount is ever pre-filled. A fixed amount is typed as a normal
// amount and sent as integer minor units of the terms' currency; more decimals
// than the currency has are refused inline, never rounded.
import React, { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { Textarea } from '@/features/shared/components/ui/textarea';
import { DataState } from '@/features/shared/components/ui/DataState';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { listLegalEntities } from '@/lib/compensationApi';
import {
  type BookingClient, type Offering, type Terms, type TermsDraft, type TermsTier, activateTerms, createClient, createOffering,
  createTerms, updateTerms,
} from '@/services/baBookingService';
import { type TermsValueError, currencyDigits, formatTermsValue, termsTextToValue, termsValueToText } from './bookingFormat';
import { Field, type K, NativeSelect, StatusBadge, useRunAction } from './shared';

// ── clients ──────────────────────────────────────────────────────────────────

export const ClientsPanel: React.FC<{ k: K; clients: BookingClient[]; onChange: () => void }> = ({ k, clients, onChange }) => {
  const { busy, run } = useRunAction(k);
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ legalName: '', contactName: '', contactEmail: '', contactPhone: '' });
  const set = (n: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF((s) => ({ ...s, [n]: e.target.value }));
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!f.legalName.trim()) return;
    const body = Object.fromEntries(Object.entries(f).map(([a, v]) => [a, v.trim()]).filter(([, v]) => v)) as { legalName: string };
    if (await run(() => createClient(body), k('clients.saved', 'Client added.'))) {
      setAdding(false); setF({ legalName: '', contactName: '', contactEmail: '', contactPhone: '' }); onChange();
    }
  };
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle>{k('clients.title', 'Clients')}</CardTitle>
          {!adding && <Button size="sm" variant="outline" onClick={() => setAdding(true)}><Plus className="w-4 h-4 mr-1" />{k('clients.add', 'Add client')}</Button>}
        </div>
        <p className="text-sm text-muted-foreground">{k('clients.help', 'Clients are business records. They don’t sign in: you act for them in the pilot.')}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {adding && (
          <form onSubmit={submit} className="space-y-3 rounded-md border border-border p-3" data-testid="client-form">
            <div className="grid gap-3 md:grid-cols-2">
              <Field label={k('clients.legal_name', 'Legal name')} htmlFor="cl-name"><Input id="cl-name" value={f.legalName} onChange={set('legalName')} required /></Field>
              <Field label={k('clients.contact_name', 'Contact person')} htmlFor="cl-cn"><Input id="cl-cn" value={f.contactName} onChange={set('contactName')} /></Field>
              <Field label={k('clients.contact_email', 'Contact email')} htmlFor="cl-ce"><Input id="cl-ce" type="email" value={f.contactEmail} onChange={set('contactEmail')} /></Field>
              <Field label={k('clients.contact_phone', 'Contact phone')} htmlFor="cl-cp"><Input id="cl-cp" value={f.contactPhone} onChange={set('contactPhone')} /></Field>
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={busy || !f.legalName.trim()}>{k('clients.save', 'Save client')}</Button>
              <Button type="button" variant="outline" onClick={() => setAdding(false)}>{k('common.cancel', 'Cancel')}</Button>
            </div>
          </form>
        )}
        {clients.length === 0 && !adding && <DataState variant="empty" compact title={k('clients.empty', 'No clients yet.')} />}
        {clients.length > 0 && (
          <ul className="divide-y divide-border text-sm">
            {clients.map((c) => (
              <li key={c.id} className="py-2">
                <p className="font-medium text-foreground">{c.legalName}</p>
                <p className="text-xs text-muted-foreground">{[c.contactName, c.contactEmail, c.contactPhone].filter(Boolean).join(' · ') || '—'}</p>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

// ── offerings ────────────────────────────────────────────────────────────────

export const OfferingsPanel: React.FC<{ k: K; offerings: Offering[]; onChange: () => void }> = ({ k, offerings, onChange }) => {
  const { busy, run } = useRunAction(k);
  const [adding, setAdding] = useState(false);
  const [entities, setEntities] = useState<{ id: string; name: string }[] | null>(null);
  const [f, setF] = useState({ entity: '', title: '', roles: '', locations: '', before: '', after: '' });
  const set = (n: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF((s) => ({ ...s, [n]: e.target.value }));
  useEffect(() => {
    if (!adding || entities) return;
    listLegalEntities().then((es) => setEntities(es.map((x: any) => ({ id: x.id, name: x.name ?? x.id })))).catch(() => setEntities([]));
  }, [adding, entities]);
  const int = (v: string) => (/^\d+$/.test(v) ? Number(v) : NaN);
  // Buffers are required, with no default (spec §2.1).
  const ready = f.entity.trim() && f.title.trim() && int(f.before) >= 0 && int(f.after) >= 0;
  const list = (v: string) => v.split(',').map((x) => x.trim()).filter(Boolean);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    if (await run(() => createOffering({
      providerLegalEntityId: f.entity.trim(), title: f.title.trim(), roleCategories: list(f.roles), locations: list(f.locations),
      bufferBeforeMin: int(f.before), bufferAfterMin: int(f.after),
    }), k('offerings.saved', 'Offering added.'))) { setAdding(false); onChange(); }
  };
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle>{k('offerings.title', 'Offerings')}</CardTitle>
          {!adding && <Button size="sm" variant="outline" onClick={() => setAdding(true)}><Plus className="w-4 h-4 mr-1" />{k('offerings.add', 'Add offering')}</Button>}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {adding && (
          <form onSubmit={submit} className="space-y-3 rounded-md border border-border p-3" data-testid="offering-form">
            <div className="grid gap-3 md:grid-cols-2">
              <Field label={k('offerings.entity', 'Providing company (legal entity)')} htmlFor="of-ent">
                {entities && entities.length > 0 ? (
                  <NativeSelect id="of-ent" value={f.entity} onChange={set('entity')} required>
                    <option value="">{k('common.choose', 'Choose…')}</option>
                    {entities.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                  </NativeSelect>
                ) : <Input id="of-ent" value={f.entity} onChange={set('entity')} placeholder={k('offerings.entity_id', 'Legal entity ID')} required />}
              </Field>
              <Field label={k('offerings.name', 'Title')} htmlFor="of-title"><Input id="of-title" value={f.title} onChange={set('title')} required /></Field>
              <Field label={k('offerings.roles', 'Role categories (comma-separated)')} htmlFor="of-roles"><Input id="of-roles" value={f.roles} onChange={set('roles')} /></Field>
              <Field label={k('offerings.locations', 'Locations (comma-separated)')} htmlFor="of-loc"><Input id="of-loc" value={f.locations} onChange={set('locations')} /></Field>
              <Field label={k('offerings.before', 'Buffer before boarding (minutes)')} htmlFor="of-bb"><Input id="of-bb" inputMode="numeric" value={f.before} onChange={set('before')} required /></Field>
              <Field label={k('offerings.after', 'Buffer after return (minutes)')} htmlFor="of-ba"><Input id="of-ba" inputMode="numeric" value={f.after} onChange={set('after')} required /></Field>
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={busy || !ready}>{k('offerings.save', 'Save offering')}</Button>
              <Button type="button" variant="outline" onClick={() => setAdding(false)}>{k('common.cancel', 'Cancel')}</Button>
            </div>
          </form>
        )}
        {offerings.length === 0 && !adding && <DataState variant="empty" compact title={k('offerings.empty', 'No offerings yet.')} />}
        {offerings.length > 0 && (
          <ul className="divide-y divide-border text-sm">
            {offerings.map((o) => (
              <li key={o.id} className="py-2">
                <p className="font-medium text-foreground">{o.title}</p>
                <p className="text-xs text-muted-foreground">
                  {k('offerings.buffers', 'Buffers: {{before}} min before, {{after}} min after', { before: o.bufferBeforeMin, after: o.bufferAfterMin })}
                </p>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

// ── terms (F) ────────────────────────────────────────────────────────────────

type TierForm = { notice: string; basis: 'percent' | 'fixed'; value: string };

const TermsForm: React.FC<{ k: K; offerings: Offering[]; existing?: Terms; onSaved: () => void; onCancel: () => void }> = ({
  k, offerings, existing, onSaved, onCancel,
}) => {
  const { busy, run } = useRunAction(k);
  const s = (v: string | number | null | undefined) => (v == null ? '' : String(v));
  const cur0 = existing?.currency ?? null;
  // Editing shows a stored fixed amount minor → major (50050 TZS → "500.50").
  const [f, setF] = useState({
    offeringId: existing?.offeringId ?? (offerings.length === 1 ? offerings[0].id : ''),
    title: existing?.title ?? '', currency: s(existing?.currency),
    baBasis: (existing?.lateCancelBaShareBasis ?? '') as '' | 'percent' | 'fixed',
    baValue: termsValueToText(existing?.lateCancelBaShareBasis, existing?.lateCancelBaShareValue, cur0),
    baConsequence: s(existing?.baCancellationConsequence), noShow: s(existing?.noShowTerms), lateEarly: s(existing?.lateEarlyDelayedTerms),
  });
  const [tiers, setTiers] = useState<TierForm[]>(() => (existing?.clientCancellationTiers ?? [])
    .map((t) => ({ notice: s(t.noticeHoursMin), basis: t.chargeBasis, value: termsValueToText(t.chargeBasis, t.chargeValue, cur0) })));
  const set = (n: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setF((x) => ({ ...x, [n]: e.target.value }));
  const num = (v: string) => (v.trim() === '' || Number.isNaN(Number(v)) ? undefined : Number(v));
  // Typed amounts → wire values (fixed: minor units of the currency), with any
  // problem shown inline under the field.
  const baParsed = f.baBasis ? termsTextToValue(f.baBasis, f.baValue, f.currency) : {};
  const tierParsed = tiers.map((t) => termsTextToValue(t.basis, t.value, f.currency));
  const valueError = (err?: TermsValueError) => {
    if (!err) return undefined;
    if (err === 'currency') return k('terms.err_currency', 'Enter the three-letter currency first: fixed amounts are in that currency.');
    if (err === 'percent') return k('terms.err_percent', 'Enter a percent from 0 to 100.');
    return k('terms.err_amount', 'Enter an amount in {{currency}} with no more than {{digits}} decimal places.', {
      currency: f.currency.trim().toUpperCase(), digits: currencyDigits(f.currency.trim().toUpperCase()),
    });
  };
  const invalid = !!baParsed.error || tierParsed.some((t) => t.error);
  const ready = !!f.offeringId && !!f.title.trim() && !invalid;

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    // Only what was typed; nothing defaults (F).
    const body: TermsDraft = {
      title: f.title.trim(),
      ...(f.currency.trim() ? { currency: f.currency.trim().toUpperCase() } : {}),
      ...(tiers.length ? {
        clientCancellationTiers: tiers.map((t, i): TermsTier => ({
          noticeHoursMin: num(t.notice) ?? null, chargeBasis: t.basis, chargeValue: tierParsed[i].value ?? null,
        })),
      } : {}),
      ...(f.baBasis ? { lateCancelBaShareBasis: f.baBasis } : {}),
      ...(baParsed.value != null ? { lateCancelBaShareValue: baParsed.value } : {}),
      ...(f.baConsequence.trim() ? { baCancellationConsequence: f.baConsequence.trim() } : {}),
      ...(f.noShow.trim() ? { noShowTerms: f.noShow.trim() } : {}),
      ...(f.lateEarly.trim() ? { lateEarlyDelayedTerms: f.lateEarly.trim() } : {}),
    };
    const out = await run(() => (existing
      ? updateTerms(existing.id, existing.version, body)
      : createTerms({ ...body, offeringId: f.offeringId, title: f.title.trim() })), k('terms.saved', 'Terms draft saved.'));
    if (out) onSaved();
  };

  const id = existing?.id ?? 'new';
  return (
    <form onSubmit={save} className="space-y-3 rounded-md border border-border p-3" data-testid="terms-form">
      <p className="text-xs text-muted-foreground">{k('terms.help', 'Type only what the company has agreed. Nothing is filled in for you. The owner activates a version once every part is complete.')}</p>
      <div className="grid gap-3 md:grid-cols-2">
        <Field label={k('terms.offering', 'Offering')} htmlFor={`t-off-${id}`}>
          <NativeSelect id={`t-off-${id}`} value={f.offeringId} onChange={set('offeringId')} disabled={!!existing} required>
            <option value="">{k('common.choose', 'Choose…')}</option>
            {offerings.map((o) => <option key={o.id} value={o.id}>{o.title}</option>)}
          </NativeSelect>
        </Field>
        <Field label={k('terms.name', 'Title')} htmlFor={`t-title-${id}`}><Input id={`t-title-${id}`} value={f.title} onChange={set('title')} required /></Field>
        <Field label={k('terms.currency', 'Currency (needed for fixed amounts)')} htmlFor={`t-cur-${id}`}><Input id={`t-cur-${id}`} value={f.currency} maxLength={3} onChange={set('currency')} /></Field>
      </div>
      <fieldset className="space-y-2">
        <legend className="text-sm font-medium text-foreground">{k('terms.tiers', 'Client cancellation charges (most notice first)')}</legend>
        {tiers.map((t, i) => (
          <div key={i} className="grid gap-2 md:grid-cols-[1fr_8rem_1fr_auto] items-end">
            <Input aria-label={k('terms.tier_notice', 'Notice of at least (hours)')} placeholder={k('terms.tier_notice', 'Notice of at least (hours)')} inputMode="numeric"
              value={t.notice} onChange={(e) => setTiers((ts) => ts.map((x, j) => (j === i ? { ...x, notice: e.target.value } : x)))} />
            <NativeSelect aria-label={k('terms.basis', 'Basis')} value={t.basis} onChange={(e) => setTiers((ts) => ts.map((x, j) => (j === i ? { ...x, basis: e.target.value as TierForm['basis'] } : x)))}>
              <option value="percent">{k('terms.basis_percent', 'Percent')}</option>
              <option value="fixed">{k('terms.basis_fixed', 'Fixed amount')}</option>
            </NativeSelect>
            <Input aria-label={k('terms.tier_value', 'Charge')} placeholder={k('terms.tier_value', 'Charge')} inputMode="decimal"
              value={t.value} onChange={(e) => setTiers((ts) => ts.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} />
            <Button type="button" variant="ghost" size="sm" aria-label={k('terms.remove_tier', 'Remove')} onClick={() => setTiers((ts) => ts.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></Button>
            {tierParsed[i]?.error && (
              <p className="text-xs text-danger-strong md:col-span-4" role="alert" data-testid={`tier-error-${i}`}>{valueError(tierParsed[i].error)}</p>
            )}
          </div>
        ))}
        <Button type="button" size="sm" variant="outline" onClick={() => setTiers((ts) => [...ts, { notice: '', basis: 'percent', value: '' }])}>
          <Plus className="w-4 h-4 mr-1" />{k('terms.add_tier', 'Add charge tier')}
        </Button>
      </fieldset>
      <div className="grid gap-3 md:grid-cols-2">
        <Field label={k('terms.ba_portion_basis', 'Ambassador’s portion of a late cancellation: basis')} htmlFor={`t-bb-${id}`}>
          <NativeSelect id={`t-bb-${id}`} value={f.baBasis} onChange={set('baBasis')}>
            <option value="">{k('common.choose', 'Choose…')}</option>
            <option value="percent">{k('terms.basis_percent', 'Percent')}</option>
            <option value="fixed">{k('terms.basis_fixed', 'Fixed amount')}</option>
          </NativeSelect>
        </Field>
        <Field label={k('terms.ba_portion_value', 'Ambassador’s portion of a late cancellation: value')} htmlFor={`t-bv-${id}`}
          hint={f.baBasis === 'fixed' ? k('terms.fixed_hint', 'A normal amount, e.g. 500 or 500.50.') : undefined}>
          <Input id={`t-bv-${id}`} inputMode="decimal" value={f.baValue} onChange={set('baValue')} aria-invalid={!!baParsed.error} />
          {baParsed.error && <p className="text-xs text-danger-strong" role="alert" data-testid="ba-value-error">{valueError(baParsed.error)}</p>}
        </Field>
        <Field label={k('terms.ba_consequence', 'If the ambassador cancels')} htmlFor={`t-bc-${id}`} className="md:col-span-2">
          <Textarea id={`t-bc-${id}`} value={f.baConsequence} onChange={set('baConsequence')} />
        </Field>
        <Field label={k('terms.no_show', 'No-show terms')} htmlFor={`t-ns-${id}`} className="md:col-span-2">
          <Textarea id={`t-ns-${id}`} value={f.noShow} onChange={set('noShow')} />
        </Field>
        <Field label={k('terms.late_early', 'Late, early or delayed departure terms')} htmlFor={`t-le-${id}`} className="md:col-span-2">
          <Textarea id={`t-le-${id}`} value={f.lateEarly} onChange={set('lateEarly')} />
        </Field>
      </div>
      <div className="flex gap-2">
        <Button type="submit" disabled={busy || !ready}>{k('terms.save', 'Save terms draft')}</Button>
        <Button type="button" variant="outline" onClick={onCancel}>{k('common.cancel', 'Cancel')}</Button>
      </div>
    </form>
  );
};

/** The amounts of a terms version: fixed ones minor → major in its currency
 *  (formatted like the fee), percent as "N%". */
const TermsAmounts: React.FC<{ t: Terms; k: K }> = ({ t, k }) => {
  const tiers = t.clientCancellationTiers ?? [];
  if (!tiers.length && t.lateCancelBaShareBasis == null) return null;
  return (
    <dl className="basis-full grid gap-x-4 gap-y-1 text-xs md:grid-cols-2" data-testid={`terms-amounts-${t.id}`}>
      {tiers.map((x, i) => (
        <div key={i}>
          <dt className="text-muted-foreground">{x.noticeHoursMin == null
            ? k('terms.tier_any_notice', 'Charge tier')
            : k('terms.tier_label', 'Notice of at least {{hours}} h', { hours: x.noticeHoursMin })}</dt>
          <dd className="text-foreground">{formatTermsValue(x.chargeBasis, x.chargeValue, t.currency)}</dd>
        </div>
      ))}
      {t.lateCancelBaShareBasis != null && (
        <div>
          <dt className="text-muted-foreground">{k('terms.ba_portion', 'Ambassador’s portion of a late cancellation')}</dt>
          <dd className="text-foreground">{formatTermsValue(t.lateCancelBaShareBasis, t.lateCancelBaShareValue, t.currency)}</dd>
        </div>
      )}
    </dl>
  );
};

export const TermsPanel: React.FC<{ k: K; terms: Terms[]; offerings: Offering[]; isOwner: boolean; onChange: () => void }> = ({
  k, terms, offerings, isOwner, onChange,
}) => {
  const confirm = useConfirm();
  const { busy, run } = useRunAction(k);
  const [editing, setEditing] = useState<{ existing?: Terms } | null>(null);
  const offeringName = (id: string) => offerings.find((o) => o.id === id)?.title ?? '—';
  const activate = async (t: Terms) => {
    const ok = await confirm({
      title: k('terms.activate_title', 'Activate these terms'),
      message: k('terms.activate_message', 'Activate “{{title}}” v{{n}} for {{offering}}? It replaces the active version, and an active version never changes.', {
        title: t.title, n: t.versionNo, offering: offeringName(t.offeringId),
      }),
      confirmLabel: k('terms.activate', 'Activate'),
    });
    if (!ok) return;
    if (await run((key) => activateTerms(t.id, { version: t.version, key }), k('terms.activated', 'Terms activated.'))) onChange();
  };
  const hasActive = terms.some((t) => t.status === 'active');
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle>{k('terms.title', 'Cancellation and no-show terms')}</CardTitle>
          {!editing && offerings.length > 0 && (
            <Button size="sm" variant="outline" onClick={() => setEditing({})}><Plus className="w-4 h-4 mr-1" />{k('terms.new', 'New terms draft')}</Button>
          )}
        </div>
        {!hasActive && <p className="text-sm text-muted-foreground" data-testid="terms-waiting">{k('confirm.no_active_terms', 'Waiting for Fern’s cancellation and no-show terms')}</p>}
      </CardHeader>
      <CardContent className="space-y-3">
        {editing && <TermsForm k={k} offerings={offerings} existing={editing.existing} onSaved={() => { setEditing(null); onChange(); }} onCancel={() => setEditing(null)} />}
        {terms.length === 0 && !editing && <DataState variant="empty" compact title={k('terms.empty', 'No terms versions yet.')} />}
        {terms.map((t) => (
          <div key={t.id} className="flex flex-wrap items-center gap-2 rounded-md border border-border p-3 text-sm" data-testid={`terms-${t.id}`}>
            <span className="font-medium text-foreground">{t.title}</span>
            <span className="text-muted-foreground">v{t.versionNo} · {offeringName(t.offeringId)}</span>
            <StatusBadge status={t.status} k={k} />
            <span className="flex-1" />
            {t.status === 'draft' && (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => setEditing({ existing: t })}>{k('terms.edit', 'Edit draft')}</Button>
            )}
            <TermsAmounts t={t} k={k} />
            {t.status === 'draft' && isOwner && (
              <Button size="sm" disabled={busy} onClick={() => activate(t)}>{k('terms.activate', 'Activate')}</Button>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
